import clsx from 'clsx';
import * as React from 'react';

import { Toolbar } from '@gitbook/spine-react';

import { CodeBlockLayoutContext, type CodeBlockLayoutOptions } from './contexts';

export interface CodeBlockProps extends Partial<CodeBlockLayoutOptions> {
    /** Header at the top of the code-block */
    header?: React.ReactNode;

    /** Footer at the bottom of the code-block */
    footer?: React.ReactNode;

    /** Toolbar to render when hovering the code block */
    toolbar?: React.ReactNode;

    /** Force the toolbar to be visible */
    showToolbar?: boolean;

    /** Content of the code-block, it should be composed of CodeBlockLine */
    children: React.ReactNode;

    /**
     * Should the code block be tranlated by Google Translate?
     * @default true
     */
    translate?: boolean;

    className?: string;
}

/**
 * Layout component for a code block.
 */
export function CodeBlock(props: CodeBlockProps): React.ReactElement {
    const {
        header,
        footer,
        toolbar,
        showToolbar = false,
        overflow = 'scroll',
        lineNumbers = false,
        translate = true,
        className,
        children,
    } = props;

    const layoutOptions = React.useMemo(() => {
        return {
            overflow,
            lineNumbers,
        };
    }, [overflow, lineNumbers]);

    return (
        <div className={clsx('group/code-block relative w-full rounded-sm bg-muted', className)}>
            {header ? <div className="pt-2">{header}</div> : null}

            {/* Content */}
            <div
                className={clsx('py-2', overflow !== 'wrap' && 'overflow-auto')}
                translate={translate ? 'yes' : 'no'}
                spellCheck={false}
            >
                <div className={clsx(overflow !== 'wrap' && 'min-w-max')}>
                    <CodeBlockLayoutContext.Provider value={layoutOptions}>
                        {children}
                    </CodeBlockLayoutContext.Provider>
                </div>
            </div>

            {footer && <div className="pb-2">{footer}</div>}

            {/* Toolbar */}

            {toolbar ? (
                <Toolbar
                    unstyled
                    contentEditable={false}
                    className={clsx(
                        'absolute top-2 right-2 flex items-center transition-all group-hover/code-block:opacity-100',
                        showToolbar ? 'opacity-100' : 'opacity-0'
                    )}
                >
                    {toolbar}
                </Toolbar>
            ) : null}
        </div>
    );
}
